import { numberFormatter } from '../../../../Utils/Formatters'
import {
  dropTimeZoneFromDate,
  getWeekNumberAndYear,
} from '../../../../shared/libraries/utils/date'
import { isDefined } from '../../../../shared/libraries/utils/object'
import { formatLastName } from '../../../../Utils/User'
import { getMissionDisplayStatus } from '../../../../shared/missionReadyToPublish'

/**
 * Local util for formatting pack size
 * @param {number} packSize
 * @param {number} householdsAmount
 * @returns {string} Pack size or approx pack size
 */
const _getPackSize = (packSize, householdsAmount) => {
  if (packSize && !isNaN(packSize)) {
    return packSize
  }
  if (householdsAmount && !isNaN(householdsAmount)) {
    if (householdsAmount > 200) {
      return `Apx. ${Math.floor(householdsAmount / 100) * 100}`
    } else {
      return `Apx. ${householdsAmount}`
    }
  }

  return '-'
}
/**
 * Formats data for table comsumption
 * @param {array} Missions
 * @returns {array} Array of object where keys are table columns names
 * @example formatData([{ id: 100023, status: 'AVAILABLE', density: 52, householdsAmount: 923, dateTime: ISO }])
 */
export const formatData = missions => {
  return missions.map(mission => {
    const { weekNumber, year } = getWeekNumberAndYear(
      new Date(dropTimeZoneFromDate(mission.dateTime)),
    )

    return {
      id: mission.id,
      // The DISPLAY status, not the raw one: the Status filter and sorter read
      // this value straight off the row, so a signed-off draft has to carry
      // 'READY_TO_PUBLISH' here or it can never be filtered for.
      'Status': getMissionDisplayStatus({
        status: mission.status,
        isReadyToPublish: mission.isReadyToPublish,
      }),
      'Density': mission.density,
      'Addresses': mission.householdsAmount,
      'Meeting Point': mission.meetingPoint,
      'Flyer Code': mission.flyerCode,
      'Flyers Distributed': mission.totalFlyersDistributed,
      'Pack Size': _getPackSize(mission.packSize, mission.householdsAmount),
      'Pay Adjustment': isDefined(mission.draftPayAdjustment)
        ? `${mission.draftPayAdjustment / 100}`
        : '-',
      'Bonus per conversion': isDefined(mission.bonusPerConversion)
        ? `${mission.bonusPerConversion / 100}`
        : '-',
      'Bonus threshold': mission.bonusThreshold
        ? `${mission.bonusThreshold}%`
        : '-',
      'Cost Per Flyer': mission.costPerFlyer ? mission.costPerFlyer : '-',
      'Week': `W${weekNumber}, ${year}`,
      'Date': mission.dateTime,
      'BA': mission.user
        ? mission.user.firstName + ' ' + formatLastName(mission.user.lastName)
        : null,
      userId: mission.user && mission.user.id,
      cityId: mission.city && mission.city.id,
      label: mission.label,
      isReadyToPublish: mission.isReadyToPublish,
      cancelled: mission.cancelled,
      boundary: mission.boundary,
    }
  })
}
/**
 * Formats data for table comsumption, counts totals for specific columns
 * @param {array} Missions
 * @returns {array} Array of object where keys are table columns names
 * @example getFooterData([{ density: 52, householdsAmount: 923 }])
 */
export const getFooterData = missions => {
  const { totalAddresses, totalDensity } = missions.reduce(
    (acc, mission) => {
      const density = Number(mission['Density'])
      const addresses = Number(mission['Addresses'])
      acc.totalDensity += isNaN(density) ? 0 : density
      acc.totalAddresses += isNaN(addresses) ? 0 : addresses
      return acc
    },
    {
      totalAddresses: 0,
      totalDensity: 0,
    },
  )

  const data = {
    'Select': 'Total:',
    'Label': numberFormatter(missions.length),
    'Density': numberFormatter(totalDensity),
    'Addresses': numberFormatter(totalAddresses),
  }

  return [data]
}

export const removeCountryDataFromMeetingPointString = address => {
  const addressParts = address.split(',')
  const street = addressParts[0]
  const secondPart = addressParts[1]
  let postcode = null
  if (secondPart) {
    postcode = secondPart.split(' ')[1]
  }
  return `${street || ''}, ${postcode || ''}`
}
